import { DataTable, useContainerHeight , EmptyState } from '@components/DataTable';
import { DataTableColumn, SelectionModel, SortModel } from '@components/DataTable/types';
import TalentMediaDialog from '@modules/talent-media/TalentMediaDialog';
import { TalentActionsCell, TalentCellData, TalentNameCell, TalentRatingCell } from '@modules/talents/components/TalentCell';
import TalentFloatingActionBar from '@modules/talents/components/TalentFloatingActionBar';
import { useTalentOperations } from '@modules/talents/context/TalentOperationsContext';
import { buildTalentFavoritesMap } from '@modules/talents/utils/talentFavoritesMap';
import { TalentSearchItem } from '@modules/talent-search/types';
import { ScoringStatus } from '@modules/scoring/types';
import { Box, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface TalentFavoritesDataTableProps {
    talents: TalentSearchItem[];
    loading: boolean;
    isLoadingMore?: boolean;
    onScrollEnd?: () => void;
    error: any;
    pagination: {
        currentPage: number;
        pageSize: number;
        totalRows: number;
        onPageChange: (page: number) => void;
        onPageSizeChange: (size: number) => void;
    };
    sortModel?: SortModel[];
    onSortChange?: (model: SortModel[]) => void;
    onRefetch: () => void;
}

/** Map a TalentSearchItem row into the normalised TalentCellData shape */
const toTalentCell = (
    row: TalentSearchItem,
    onOpenMedia: (talentId: string, defaultTab: 'notes' | 'files') => void,
): TalentCellData => ({
    _id: row.talent._id,
    fullName: row.talent.fullName ?? '',
    firstName: row.talent.firstName ?? '',
    lastName: row.talent.lastName ?? '',
    photo: row.talent.profilePicture?.url || '',
    active: row.talent.active,
    cvUserData: row.talent,
    currentStatusUser: row.talent.currentStatusUser,
    upercutAverageRating: row.talent?.upercutAverageRating,
    upercutNotes: row.talent?.upercutNotes,
    upercutFiles: row.talent?.upercutFiles,
    upercutFavorites: row.talent?.upercutFavorites,
    onOpenMedia: (defaultTab) => onOpenMedia(row.talent._id, defaultTab),
    detailPath: `/talents/${row.talent?._id}/detail`,
});

/**
 * TalentFavoritesDataTable Component
 * Displays paginated list of favorite talents using DataTable.
 * Uses shared TalentCell components and TalentOperationsContext.
 */
const TalentFavoritesDataTable: React.FC<TalentFavoritesDataTableProps> = ({
    talents,
    loading,
    isLoadingMore = false,
    onScrollEnd,
    error,
    pagination,
    sortModel,
    onSortChange,
    onRefetch,
}) => {
    const navigate = useNavigate();
    const [mediaDialogState, setMediaDialogState] = useState<{
        isOpen: boolean;
        talentId: string;
        defaultTab: 'notes' | 'files' | 'comments';
        score?: number | null;
        scoreExplainer?: string;
        scoringStatus?: ScoringStatus;
    }>({
        isOpen: false,
        talentId: '',
        defaultTab: 'notes',
    });

    const handleOpenMediaDialog = useCallback((talentId: string, defaultTab: 'notes' | 'files') => {
        setMediaDialogState({
            isOpen: true,
            talentId,
            defaultTab,
        });
    }, []);

    const handleCloseMediaDialog = useCallback(() => {
        setMediaDialogState(prev => ({
            ...prev,
            isOpen: false,
        }));
    }, []);

    const {
        selectedTalents,
        toggleTalent,
        isSelected,
        currentStatus,
        updateRating,
        updateStatus,
        isUpdatingRating,
        isUpdatingStatus,
        clearSelection
    } = useTalentOperations();

    // Prepare talent favorites map for multi-selection
    const talentFavoritesMap = buildTalentFavoritesMap(selectedTalents, talents);

    const columns = useMemo<DataTableColumn<TalentSearchItem>[]>(() => [
        {
            id: 'name',
            sortable: true,
            label: 'Nom',
            sticky: true,
            renderCell: ({ row }) => <TalentNameCell talent={toTalentCell(row, handleOpenMediaDialog)} />,
        },
        {
            id: 'rating',
            sortable: true,
            label: 'Évaluation',
            minWidth: 180,
            renderCell: ({ row }) => <TalentRatingCell talent={toTalentCell(row, handleOpenMediaDialog)} />,
        },
        {
            id: 'city',
            sortable: true,
            label: 'Ville',
            minWidth: 120,
            renderCell: ({ row }) => row.talent?.city || '-',
        },
        {
            id: 'experience',
            sortable: true,
            label: 'Expérience',
            minWidth: 120,
            renderCell: ({ row }) => {
                const years = row.talent?.yearsOfExperience;
                return typeof years === 'number' ? `${Math.floor(years)} ans` : '-';
            },
        },
        {
            id: 'contract',
            sortable: false,
            label: 'Contrat',
            minWidth: 100,
        },
        {
            id: 'education',
            sortable: false,
            label: 'Étude',
            minWidth: 100,
        },
        {
            id: 'applications',
            sortable: true,
            label: 'Application',
            minWidth: 100,
            renderCell: ({ row }) => row.talent?.jobApplications?.length ?? 0,
        },
        {
            id: 'createdAt',
            sortable: true,
            label: 'Créé',
            minWidth: 140,
            renderCell: ({ row }) => {
                const createdAt = row.favorite?.createdAt;
                return createdAt ? format(new Date(createdAt), 'dd.MM.yy(HH:mm)') : '-';
            },
        },
        {
            id: 'actions',
            label: '',
            minWidth: 170,
            renderCell: ({ row }) => (
                <TalentActionsCell
                    talent={toTalentCell(row, handleOpenMediaDialog)}
                    onView={() => navigate(`/talents/${row.talent?._id}/detail`)}
                />
            ),
        },
    ], [navigate, handleOpenMediaDialog]);

    const selectionModel = useMemo<SelectionModel>(() => {
        const selectedRows = talents
            .filter(item => isSelected(item.talent._id))
            .map(item => item.talent._id);

        return {
            selectedRows,
            isAllSelected: talents.length > 0 && selectedRows.length === talents.length,
            isSomeSelected: selectedRows.length > 0 && selectedRows.length < talents.length,
        };
    }, [talents, isSelected]);

    const handleSelectionModelChange = (newModel: SelectionModel) => {
        if (newModel.selectedRows.length === 0) {
            clearSelection();
            return;
        }

        // Get currently selected talent IDs
        const currentlySelected = selectedTalents.map(t => t._id);

        // Get newly selected talent IDs from the model
        const newlySelected = newModel.selectedRows
            .map(rowId => typeof rowId === 'string' ? rowId : talents[rowId as number]?.talent._id)
            .filter((id): id is string => Boolean(id));

        // Toggle talents that changed selection state
        talents.forEach(item => {
            const talentId = item.talent._id;
            const isCurrentlySelected = currentlySelected.includes(talentId);
            const shouldBeSelected = newlySelected.includes(talentId);

            if (isCurrentlySelected !== shouldBeSelected) {
                const status = item.talent?.currentStatusUser;
                toggleTalent(talentId, status);
            }
        });
    };

    const { containerHeight, containerRef } = useContainerHeight();

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
                <Typography variant="h6">Erreur lors du chargement des favoris</Typography>
                <Typography variant="body2">{error.message}</Typography>
            </Box>
        );
    }

    return (
        <Box ref={containerRef}  sx={{
                flex: 1, width: '100%',
                "& .MuiTableHead-root": {
                    zIndex: 103
                },
                // second cell from row should have overflow visible to show the full name and the company name
                '& .MuiTableRow-root': {
                    '& .MuiTableCell-root:nth-child(2)': {
                        zIndex: 102,
                        overflow: 'visible !important',
                        "& > .MuiBox-root": {
                            overflow: 'visible !important',
                        }
                    },
                }
            }}
        >
            <DataTable<TalentSearchItem>
                rows={talents}
                columns={columns}
                loading={loading}
                isLoadingMore={isLoadingMore}
                onScrollEnd={onScrollEnd}
                sortModel={sortModel}
                onSortModelChange={onSortChange}
                pagination={pagination}
                checkboxSelection
                selectionModel={selectionModel}
                onSelectionModelChange={handleSelectionModelChange}
                stickyHeader
                height={containerHeight}
                aria-label="favorites table"
                noDataComponent={
                    <Grid item xs={12}>
                        <EmptyState
                            title="Aucun talent en favori"
                            subtitle="Ajoutez des talents à vos favoris pour les retrouver ici."
                        />
                    </Grid>
                }
            />
            <TalentMediaDialog
                isOpen={mediaDialogState.isOpen}
                onClose={handleCloseMediaDialog}
                talentId={mediaDialogState.talentId}
                defaultTab={mediaDialogState.defaultTab}
                score={mediaDialogState.score}
                scoreExplainer={mediaDialogState.scoreExplainer}
                scoringStatus={mediaDialogState.scoringStatus}
            />

            {selectedTalents.length > 0 && (
                <TalentFloatingActionBar
                    selectedTalentIds={selectedTalents.map((talent) => talent._id)}
                    onSendMessage={() => console.log('Send message clicked', selectedTalents)}
                    onUpdateRating={(rating) => {
                        const talentIds = selectedTalents.map((talent) => talent._id);
                        updateRating(talentIds, rating);
                    }}
                    onStatusChange={(status) => {
                        const talentIds = selectedTalents.map((talent) => talent._id);
                        // Convert avatar status to backend status format
                        const backendStatus = status === 'nothing' ? '' : status;
                        updateStatus(talentIds, backendStatus);
                    }}
                    status={currentStatus}
                    isUpdatingRating={isUpdatingRating}
                    isUpdatingStatus={isUpdatingStatus}
                    talentFavoritesMap={talentFavoritesMap}
                />
            )}

        </Box>
    );
};

export default TalentFavoritesDataTable;
